import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import API from '../services/api';
import COMPANY_INFO from '../data/companyInfo';
import SignatureLines from './SignatureLines';
import printWhenReady from '../utils/printWhenReady';
import './InvoicePrintView.css';
import { formatDay, todayDR } from '../utils/dates';
import { useFileName } from '../utils/fileName';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const METHOD = { efectivo: 'Efectivo', transferencia: 'Transferencia', tarjeta: 'Tarjeta', cheque: 'Cheque' };
const STATE = { active: 'Al día', overdue: 'Vencido', paid: 'Saldado', closed: 'Cerrado' };

// A compromiso's statement (Gastos → Compromisos → Imprimir): every payment made toward it with the balance after each, for
// our records and to hand to the creditor if a payment is disputed. With ?comprobantes=1 each payment's voucher follows on
// its own page (images; a PDF voucher is named there and opened from Ventas). Saved as PDF under the compromiso's name.
const CommitmentPrintView = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const withVouchers = searchParams.get('comprobantes') === '1';
  const [c, setC] = useState(null);
  const [vouchers, setVouchers] = useState(null); // { [expenseId]: { url, type } }
  const [error, setError] = useState('');

  useEffect(() => {
    API.get(`/commitments/${id}`).then(res => setC(res.data)).catch(err => setError(err.response?.data?.message || 'No se pudo cargar el compromiso.'));
  }, [id]);

  // The vouchers come through the API (they need the session), as local object URLs for the <img> tags.
  useEffect(() => {
    if (!c) return undefined;
    if (!withVouchers) { setVouchers({}); return undefined; }
    let alive = true;
    const urls = [];
    Promise.all(c.entries.filter(e => e.hasVoucher).map(e => API.get(`/expenses/${e.expense}/voucher`, { responseType: 'blob' })
      .then(res => { const url = URL.createObjectURL(res.data); urls.push(url); return [e.expense, { url, type: res.data.type }]; })
      .catch(() => [e.expense, { failed: true }])))
      .then(list => { if (alive) setVouchers(Object.fromEntries(list)); });
    return () => { alive = false; urls.forEach(u => URL.revokeObjectURL(u)); };
  }, [c, withVouchers]);

  useEffect(() => {
    if (!c || !vouchers) return undefined;
    let cancelPrint = () => {};
    const t = setTimeout(() => { cancelPrint = printWhenReady(8000); }, 350);
    return () => { clearTimeout(t); cancelPrint(); };
  }, [c, vouchers]);

  // Saved as PDF under the compromiso's own name (the owner's choice for these reports).
  useFileName(c && c.name.replace(/[\\/:*?"<>|]+/g, ' ').trim());

  if (error) return <div className="p-4 text-danger">{error}</div>;
  if (!c || !vouchers) return <div className="p-4 text-muted">Cargando compromiso...</div>;

  const withFile = c.entries.map((e, i) => ({ ...e, n: i + 1 })).filter(e => e.hasVoucher);

  return (
    <div className="invoice-page-wrapper">
      <div className="no-print text-center mb-3">
        <button className="btn btn-primary" onClick={() => window.print()}>
          <i className="bi bi-printer" /> Imprimir / Guardar como PDF
        </button>
      </div>

      <div className="invoice-paper">
        <div className="invoice-body">
          <div className="invoice-header-row">
            <div className="invoice-company-block">
              <img src="/CGCLogo.png" alt="Caribbean Gas Company" className="invoice-logo" />
              <div style={{ fontWeight: 700, color: '#0E1A24' }}>{COMPANY_INFO.name}</div>
              <div>RNC: {COMPANY_INFO.rnc}</div>
              <div>{COMPANY_INFO.address}</div>
              <div>{COMPANY_INFO.phone} · {COMPANY_INFO.email}</div>
            </div>
            <div className="invoice-meta-block">
              <div className="invoice-title">ESTADO DE PAGOS</div>
              <div><strong>{c.name}</strong></div>
              <div>Emitido: {formatDay(todayDR())}</div>
              {c.dueDate && <div>Fecha límite: {formatDay(c.dueDate)}</div>}
              <div>Estado: {STATE[c.state] || c.state}</div>
            </div>
          </div>

          <div className="invoice-bill-to">
            <div className="label">Acreedor</div>
            <div style={{ fontWeight: 600 }}>{c.payee || '—'}</div>
            {c.accounts.length > 0 && (
              <div className="invoice-client-details">
                Cuenta{c.accounts.length === 1 ? '' : 's'} de pago: {c.accounts.map(a => `${a.number}${a.label ? ` (${a.label})` : ''}`).join(' · ')}
              </div>
            )}
          </div>

          <table className="invoice-table">
            <thead>
              <tr>
                <th className="text-center">#</th>
                <th>Fecha</th>
                <th>Concepto</th>
                <th>Método</th>
                <th>Cuenta destino</th>
                <th>Referencia</th>
                <th className="text-end">Monto</th>
                <th className="text-end">Pendiente</th>
              </tr>
            </thead>
            <tbody>
              {c.entries.map((e, i) => (
                <tr key={e.expense}>
                  <td className="text-center">{i + 1}</td>
                  <td>{formatDay(e.date)}</td>
                  <td>{e.description || e.vendor || e.category}{e.hasVoucher && withVouchers ? ' *' : ''}</td>
                  <td>{METHOD[e.method] || e.method}</td>
                  <td>{e.toAccount || '—'}</td>
                  <td>{e.reference || '—'}</td>
                  <td className="text-end">{money(e.amount)}</td>
                  <td className="text-end">{money(e.balanceAfter)}</td>
                </tr>
              ))}
              {c.entries.length === 0 && <tr><td colSpan={8} className="text-center">Todavía no hay pagos registrados.</td></tr>}
            </tbody>
          </table>

          <div className="invoice-totals">
            <div className="invoice-totals-box">
              <div className="invoice-totals-row"><span>Total adeudado</span><span>{money(c.amount)}</span></div>
              <div className="invoice-totals-row"><span>Pagado ({c.payments} pago{c.payments === 1 ? '' : 's'})</span><span>{money(c.paid)}</span></div>
              <div className="invoice-totals-row grand"><span>{c.balance < -0.005 ? 'Pagado de más' : 'Pendiente'}</span><span>{money(Math.abs(c.balance))}</span></div>
            </div>
          </div>

          {c.notes && (
            <div className="invoice-bill-to">
              <div className="label">Notas</div>
              <div style={{ whiteSpace: 'pre-line' }}>{c.notes}</div>
            </div>
          )}

          <div className="invoice-disclaimer">
            Relación de los pagos realizados por {COMPANY_INFO.name} a este compromiso según sus registros al {formatDay(todayDR())}.
            {withVouchers && withFile.length > 0 && ' Los pagos marcados con * llevan su comprobante en las páginas siguientes.'}
          </div>
        </div>
        <div className="invoice-bottom">
          <SignatureLines clientName={c.payee} receiverLabel="Recibido por el acreedor" />
        </div>
      </div>

      {withVouchers && withFile.map(e => {
        const v = vouchers[e.expense] || {};
        return (
          <div key={e.expense} className="invoice-paper voucher-page">
            <div className="invoice-body">
              <div className="invoice-header-row">
                <div className="invoice-company-block">
                  <div style={{ fontWeight: 700, color: '#0E1A24' }}>{c.name} — comprobante del pago #{e.n}</div>
                  <div>{formatDay(e.date)} · {money(e.amount)} · {METHOD[e.method] || e.method}{e.reference ? ` · Ref. ${e.reference}` : ''}</div>
                  {e.toAccount && <div>Cuenta destino: {e.toAccount}</div>}
                </div>
              </div>
              {v.url && v.type?.startsWith('image/') && <img src={v.url} alt={`Comprobante del pago ${e.n}`} className="voucher-image" />}
              {v.url && !v.type?.startsWith('image/') && <div className="invoice-disclaimer">Este comprobante es un PDF: se entrega como archivo aparte (Ventas → Gastos → Compromisos).</div>}
              {v.failed && <div className="invoice-disclaimer">No se pudo cargar este comprobante.</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CommitmentPrintView;
